import React from 'react';
import { cn } from '@crisellevaldez/speculo-ui';
import { TimestampLabel } from '../../../common/TimestampLabel';
import type { ConsentEventType, ConsentHistoryEvent } from './consent.types';
import { EmptyStateWithIcon } from '../../../common/EmptyStateWithIcon';
import { linkify } from '../../../../utils/linkify';

/** The event title already names the action, so tone is carried by the dot alone. */
const EVENT_DOT: Record<ConsentEventType, string> = {
  optin: 'border-green-600',
  optout: 'border-muted-foreground',
  modified: 'border-amber-700',
  imported: 'border-muted-foreground',
};

interface ConsentHistoryTimelineProps {
  events: ConsentHistoryEvent[];
  timeZone?: string;
}

export const ConsentHistoryTimeline: React.FC<ConsentHistoryTimelineProps> = ({
  events,
  timeZone,
}) => {
  if (events.length === 0) {
    // The shared empty state the sibling tabs use — this one had hand-rolled
    // its own, so it read a size and weight lighter than the rest.
    return <EmptyStateWithIcon message="No consent history yet." />;
  }

  return (
    <div>
      {events.map((event, index) => (
        <ConsentHistoryRow
          key={event.id}
          event={event}
          isLast={index === events.length - 1}
          timeZone={timeZone}
        />
      ))}
    </div>
  );
};

const ConsentHistoryRow: React.FC<{
  event: ConsentHistoryEvent;
  isLast: boolean;
  timeZone?: string;
}> = ({ event, isLast, timeZone }) => (
  <div className={cn('relative flex gap-4', isLast ? 'pb-0' : 'pb-6')}>
    <div className="relative flex w-[9px] shrink-0 flex-col items-center">
      <span
        className={cn(
          'z-10 mt-1 h-[9px] w-[9px] rounded-full border-2 bg-card',
          EVENT_DOT[event.type]
        )}
      />
      {!isLast && <span className="mt-0.5 w-px flex-1 bg-foreground/15" />}
    </div>

    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-semibold tracking-tight text-foreground">
          {event.title}
        </span>
        <TimestampLabel value={event.occurredAt} timezone={timeZone} className="shrink-0" />
      </div>

      <div className="flex flex-wrap items-center gap-x-[7px] gap-y-0.5 text-xs text-muted-foreground xl:text-sm">
        <span>{event.source}</span>
        <span className="text-foreground/20">·</span>
        <span>
          {event.actor}
          {event.actorRole && (
            <span className="text-muted-foreground/70"> ({event.actorRole})</span>
          )}
        </span>
      </div>

      {event.note && (
        <div className="border-l-2 border-foreground/20 py-px pl-[11px] text-xs text-muted-foreground [overflow-wrap:anywhere] xl:text-sm">
          {linkify(event.note, { shortenLabels: true })}
        </div>
      )}
    </div>
  </div>
);
